import { useEffect, type ReactNode } from 'react';
import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  InputHTMLAttributes,
  LabelHTMLAttributes,
  TdHTMLAttributes,
  ThHTMLAttributes,
} from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/cn';

/* ── Button ─────────────────────────────────────────────────────────── */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        secondary: 'border border-border bg-card text-foreground hover:bg-accent',
        ghost: 'text-foreground hover:bg-accent hover:text-accent-foreground',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
      },
      size: { default: 'h-9 px-4 py-2', sm: 'h-8 rounded-md px-3 text-xs', icon: 'h-9 w-9' },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);
export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}
export function Button({ className, variant, size, ...props }: ButtonProps): JSX.Element {
  return <button className={cn(buttonVariants({ variant, size }), className)} {...props} />;
}

/* ── Card ───────────────────────────────────────────────────────────── */
export function Card({ className, ...p }: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return <div className={cn('rounded-lg border bg-card text-card-foreground shadow-sm', className)} {...p} />;
}
export function CardHeader({ className, ...p }: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return <div className={cn('flex flex-col gap-1 p-5', className)} {...p} />;
}
export function CardTitle({ className, ...p }: HTMLAttributes<HTMLHeadingElement>): JSX.Element {
  return <h3 className={cn('font-semibold leading-none tracking-tight', className)} {...p} />;
}
export function CardDescription({ className, ...p }: HTMLAttributes<HTMLParagraphElement>): JSX.Element {
  return <p className={cn('text-sm text-muted-foreground', className)} {...p} />;
}
export function CardContent({ className, ...p }: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return <div className={cn('p-5 pt-0', className)} {...p} />;
}

/* ── Form ───────────────────────────────────────────────────────────── */
export function Input({ className, ...p }: InputHTMLAttributes<HTMLInputElement>): JSX.Element {
  return (
    <input
      className={cn(
        'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...p}
    />
  );
}
export function Label({ className, ...p }: LabelHTMLAttributes<HTMLLabelElement>): JSX.Element {
  return <label className={cn('mb-1.5 block text-sm font-medium text-foreground', className)} {...p} />;
}

/* ── Badge ──────────────────────────────────────────────────────────── */
const badgeVariants = cva('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', {
  variants: {
    variant: {
      default: 'bg-secondary text-secondary-foreground',
      primary: 'bg-accent text-accent-foreground',
      success: 'bg-status-ok/15 text-status-ok',
      warning: 'bg-status-terminal/15 text-status-terminal',
      danger: 'bg-destructive/15 text-destructive',
      purple: 'bg-purple-500/15 text-purple-600 dark:text-purple-300',
    },
  },
  defaultVariants: { variant: 'default' },
});
export interface BadgeProps extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}
export function Badge({ className, variant, ...p }: BadgeProps): JSX.Element {
  return <span className={cn(badgeVariants({ variant }), className)} {...p} />;
}

/* ── Table ──────────────────────────────────────────────────────────── */
export function Table({ className, ...p }: HTMLAttributes<HTMLTableElement>): JSX.Element {
  return (
    <div className="w-full overflow-x-auto">
      <table className={cn('w-full caption-bottom text-sm', className)} {...p} />
    </div>
  );
}
export function THead(p: HTMLAttributes<HTMLTableSectionElement>): JSX.Element {
  return <thead {...p} />;
}
export function TBody(p: HTMLAttributes<HTMLTableSectionElement>): JSX.Element {
  return <tbody {...p} />;
}
export function TR({ className, ...p }: HTMLAttributes<HTMLTableRowElement>): JSX.Element {
  return <tr className={cn('border-b transition-colors hover:bg-muted/40', className)} {...p} />;
}
export function TH({ className, ...p }: ThHTMLAttributes<HTMLTableCellElement>): JSX.Element {
  return <th className={cn('h-10 px-3 text-left align-middle text-xs font-medium text-muted-foreground', className)} {...p} />;
}
export function TD({ className, ...p }: TdHTMLAttributes<HTMLTableCellElement>): JSX.Element {
  return <td className={cn('px-3 py-2.5 align-middle', className)} {...p} />;
}

/* ── Feedback ───────────────────────────────────────────────────────── */
export function Spinner({ className }: { className?: string }): JSX.Element {
  return (
    <span
      role="status"
      aria-label="loading"
      className={cn('inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent', className)}
    />
  );
}
export function PageLoader(): JSX.Element {
  return (
    <div className="flex items-center justify-center py-24 text-muted-foreground">
      <Spinner className="h-6 w-6" />
    </div>
  );
}
const alertVariants = cva('rounded-md border px-3 py-2 text-sm', {
  variants: {
    kind: {
      error: 'border-destructive/30 bg-destructive/10 text-destructive',
      warn: 'border-status-terminal/30 bg-status-terminal/10 text-status-terminal',
      info: 'border-primary/30 bg-primary/10 text-foreground',
    },
  },
  defaultVariants: { kind: 'error' },
});
export interface AlertProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {}
export function Alert({ className, kind, ...p }: AlertProps): JSX.Element {
  return <div role="alert" className={cn(alertVariants({ kind }), className)} {...p} />;
}
export function Skeleton({ className }: { className?: string }): JSX.Element {
  return <div className={cn('animate-pulse rounded-md bg-muted', className)} />;
}
export function EmptyState({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed p-10 text-center">
      <p className="font-medium">{title}</p>
      {hint && <p className="max-w-md text-sm text-muted-foreground">{hint}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

/* ── Modal ──────────────────────────────────────────────────────────── */
const modalSizes = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
} as const;

/**
 * Schlichter, zugänglicher Dialog (Overlay + Panel). Schließt per Escape, Klick auf das Overlay und
 * über den ×-Button. Inhalt scrollt bei Überlänge; der Footer bleibt optional unten.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'lg',
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: keyof typeof modalSizes;
}): JSX.Element | null {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:p-6"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          'my-6 flex w-full flex-col rounded-lg border bg-card text-card-foreground shadow-lg',
          modalSizes[size],
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 border-b p-5">
          <h2 className="text-lg font-semibold leading-none tracking-tight">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 items-center justify-center rounded-md text-xl leading-none text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            ×
          </button>
        </div>
        <div className="overflow-y-auto p-5">{children}</div>
        {footer && (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t p-4">{footer}</div>
        )}
      </div>
    </div>
  );
}
